import { ChevronRight } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import {
  buildSentence,
  buildStackSentence,
  compactTime,
  entryPrimary,
  getIconKind,
  isEntryUnread,
  showsQuote,
} from '../utils/notificationInbox'
import { NotificationAvatar } from './NotificationAvatar'
import type { NotificationDocument } from '@/lib/db/rxdb/schemas/notification'
import type { NotificationItemProps } from '../types'
import type { NotificationLookups } from '../hooks/useNotificationLookups'
import type { SentenceDescriptor } from '../utils/notificationInbox'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { cn } from '@/lib/utils'

function useCompactTimeLabel() {
  const { t } = useTranslation('notifications')
  const formatters = useDateTimeFormatters()
  return (timestamp: number, now: number) => {
    const parts = compactTime(timestamp, now)
    switch (parts.unit) {
      case 'now':
        return t('time.now')
      case 'minutes':
        return t('time.minutes', { count: parts.value })
      case 'hours':
        return t('time.hours', { count: parts.value })
      case 'weekday':
        return formatters.date(new Date(timestamp), { weekday: 'short' })
      case 'date':
        return formatters.date(new Date(timestamp), { month: 'short', day: 'numeric' })
    }
  }
}

/** Renders a sentence descriptor with the actor (or count) in bold. */
export function NotificationSentence({
  descriptor,
  title,
  className,
}: {
  descriptor: SentenceDescriptor
  title: string
  className?: string
}) {
  const { t } = useTranslation('notifications')
  if (descriptor.useTitle) {
    return <span className={cn('font-semibold', className)}>{title}</span>
  }
  return (
    <span className={className}>
      <Trans
        t={t}
        i18nKey={descriptor.i18nKey}
        values={descriptor.values}
        count={typeof descriptor.values.count === 'number' ? descriptor.values.count : undefined}
        components={{ b: <span className="font-semibold text-foreground" /> }}
      />
    </span>
  )
}

export function describeNotification(
  notification: NotificationDocument,
  lookups: NotificationLookups,
): SentenceDescriptor {
  return buildSentence(notification, lookups.actorLabel(notification.createdBy))
}

export function NotificationItem({
  entry,
  lookups,
  now,
  isSelected = false,
  isExpanded = false,
  onSelect,
  onToggleExpand,
}: NotificationItemProps) {
  const { t } = useTranslation('notifications')
  const timeLabel = useCompactTimeLabel()
  const primary = entryPrimary(entry)
  const subject = lookups.resolveSubject(primary)
  const isUnread = isEntryUnread(entry)
  const isStack = entry.kind === 'stack'
  const descriptor = isStack
    ? buildStackSentence(entry.notifications.length, subject.identifier || subject.title)
    : describeNotification(primary, lookups)
  const quote = !isStack && showsQuote(primary) ? primary.message : null
  const actorMember = lookups.memberByUserId.get(primary.createdBy)

  return (
    <div
      role="option"
      aria-selected={isSelected}
      data-testid="notification-row"
      data-entry-id={entry.id}
      data-unread={isUnread || undefined}
      onClick={() => onSelect(entry)}
      className={cn(
        'group relative flex cursor-pointer gap-2.5 rounded-lg py-2.5 pl-2 pr-3 transition-colors',
        isSelected
          ? 'bg-card shadow-[0_1px_2px_rgb(0_0_0/0.06)] ring-1 ring-border'
          : 'hover:bg-card/60',
      )}
    >
      <span className="flex w-2 shrink-0 justify-center pt-3">
        {isUnread && (
          <span className="size-1.5 rounded-full bg-brand" data-testid="notification-unread-dot">
            <span className="sr-only">{t('unread')}</span>
          </span>
        )}
      </span>
      <NotificationAvatar
        member={actorMember}
        label={lookups.actorLabel(primary.createdBy)}
        kind={getIconKind(primary)}
        className="mt-0.5"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {isStack && (
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? t('stack.collapse') : t('stack.expand')}
              onClick={(event) => {
                event.stopPropagation()
                onToggleExpand(entry.id)
              }}
              className="-ml-0.5 flex size-4 shrink-0 items-center justify-center self-center rounded text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <ChevronRight
                className={cn('size-3.5 transition-transform', isExpanded && 'rotate-90')}
              />
            </button>
          )}
          <NotificationSentence
            descriptor={descriptor}
            title={primary.title}
            className="min-w-0 flex-1 truncate text-[13.5px] leading-5 text-foreground"
          />
          <time
            dateTime={new Date(primary.createdOn).toISOString()}
            className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
          >
            {timeLabel(primary.createdOn, now)}
          </time>
        </div>
        <p className="mt-0.5 flex min-w-0 items-baseline gap-1.5 text-[12px] leading-4 text-muted-foreground">
          {subject.identifier && (
            <span className="shrink-0 font-mono text-[11px] uppercase tracking-tight">
              {subject.identifier}
            </span>
          )}
          <span className="truncate">{subject.title}</span>
        </p>
        {quote && (
          <p className="mt-1.5 truncate rounded-md bg-secondary px-2 py-1 text-[12.5px] leading-5 text-foreground/90">
            {quote}
          </p>
        )}
        {isStack && isExpanded && (
          <ul className="mt-2 space-y-1.5" data-testid="notification-stack-updates">
            {entry.notifications.map((n) => (
              <li key={n.id} className="flex items-start gap-2 text-[12.5px] leading-5">
                <span
                  className={cn(
                    'mt-2 size-1 shrink-0 rounded-full',
                    n.isRead ? 'bg-muted-foreground/50' : 'bg-brand',
                  )}
                />
                <span className="min-w-0 flex-1 text-muted-foreground">
                  <NotificationSentence
                    descriptor={describeNotification(n, lookups)}
                    title={n.title}
                  />
                  {showsQuote(n) && (
                    <span className="text-muted-foreground">
                      {': '}
                      {t('stack.quoted', { text: n.message ?? '' })}
                    </span>
                  )}
                </span>
                <time
                  dateTime={new Date(n.createdOn).toISOString()}
                  className="shrink-0 text-[11px] tabular-nums text-muted-foreground"
                >
                  {timeLabel(n.createdOn, now)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
